/*
 * dpd-pickup.js: behaviour for snippets/dpd-pickup-card.liquid.
 * Loaded from layout/theme.liquid on every page, not inline in the snippet:
 * the mini-cart drawer is fetched via the Section Rendering API and inserted
 * with innerHTML, so an inline <script> in it never executes. All handlers are
 * delegated on document, so cards rendered later are covered.
 *
 * Cart cards save the point as two cart attributes; the card on
 * /pages/dpd-pickup (context "order") saves onto an existing order through
 * skylit-admin, using the signed ?order=&token= link staff sent.
 */
(function () {
  if (window.__skylitDpdPickup) return;
  window.__skylitDpdPickup = true;

  var API = 'https://admin.skylitstudio.co.uk/api/dpd-pickup';
  var ATTR_CODE = 'DPD pickup code';
  var ATTR_POINT = 'DPD pickup point';
  var POSTCODE_KEY = 'skylit-dpd-postcode';

  var resultsByCard = new WeakMap();
  var writeQueue = Promise.resolve();
  var pendingWrites = 0;

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function q(card, sel) { return card.querySelector(sel); }
  function isOrderCard(card) { return card.getAttribute('data-context') === 'order'; }
  function lineFor(p) { return [p.name, p.street, (p.town + ' ' + p.postcode).trim()].filter(Boolean).join(', '); }
  function remember(postcode) { try { sessionStorage.setItem(POSTCODE_KEY, postcode); } catch (e) { /* private mode */ } }
  var POINTS_KEY = 'skylit-dpd-points';
  function cachePoint(p) {
    try {
      var all = JSON.parse(sessionStorage.getItem(POINTS_KEY) || '{}');
      all[p.code] = p;
      sessionStorage.setItem(POINTS_KEY, JSON.stringify(all));
    } catch (e) { /* private mode */ }
  }
  function cachedPoint(code) {
    try { return JSON.parse(sessionStorage.getItem(POINTS_KEY) || '{}')[code] || null; } catch (e) { return null; }
  }
  // The postcode at the end of a saved point line ("..., Coventry CV5 6JU").
  function lineToPostcode(line) {
    var m = /([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i.exec(String(line || '').trim());
    return m ? m[1].toUpperCase() : '';
  }
  function recalled() { try { return sessionStorage.getItem(POSTCODE_KEY) || ''; } catch (e) { return ''; } }

  function setStatus(card, text, isError) {
    var el = q(card, '[data-dpd-status]');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'dpd-pick__status' + (isError ? ' is-error' : '');
  }

  // ---- Cart writes ----------------------------------------------------------
  // /cart/update.js saves the whole cart it read, so a write landing while a
  // customizer removes a line can put the line back. Wait for the theme's
  // edit-in-flight flag (cart-line-pairing.js) to clear, send only our two
  // keys, and run our writes one at a time.
  function whenCartSettles() {
    return new Promise(function (resolve) {
      var waited = 0;
      (function check() {
        if (!window.__skylitCartEditInFlight || waited >= 5000) return resolve();
        waited += 200;
        setTimeout(check, 200);
      })();
    });
  }

  function saveToCart(code, point) {
    var attributes = {};
    attributes[ATTR_CODE] = code;
    attributes[ATTR_POINT] = point;
    var url = (window.theme && window.theme.routes && window.theme.routes.cart_update_url) || '/cart/update.js';
    pendingWrites++;
    writeQueue = writeQueue.then(whenCartSettles).then(function () {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).then(function (r) { return r.ok; });
    }).catch(function () { return false; }).then(function (ok) {
      pendingWrites--;
      return ok;
    });
    return writeQueue;
  }

  // Tapping Checkout straight after choosing would abort the write. Hold the
  // click until it lands (capped), as gift-note-card.js does for notes.
  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"]');
    if (!btn || btn.__dpdRelease || pendingWrites === 0) return;
    e.preventDefault();
    e.stopPropagation();
    var done = false;
    function release() {
      if (done) return;
      done = true;
      btn.__dpdRelease = true;
      btn.click();
      btn.__dpdRelease = false;
    }
    writeQueue.then(release, release);
    setTimeout(release, 4000);
  }, true);

  // ---- Rendering ------------------------------------------------------------
  function showFinder(card, open) {
    var finder = q(card, '[data-dpd-finder]');
    var opener = q(card, '[data-dpd-open]');
    if (finder) finder.hidden = !open;
    if (opener) opener.hidden = open || card.classList.contains('is-chosen');
    if (open) {
      var input = q(card, '[data-dpd-postcode]');
      if (input && !input.value) input.value = recalled() || card.getAttribute('data-home-postcode') || '';
      if (!input || isOrderCard(card)) return;
      // A postcode we already know (last search, or the saved address): show the points straight away.
      if (input.value && !resultsByCard.has(card)) search(card);
      else input.focus();
    }
  }

  function applyChosen(card, code, point) {
    var chosen = !!code;
    card.classList.toggle('is-chosen', chosen);
    card.setAttribute('data-code', code || '');
    card.setAttribute('data-point', point || '');
    var text = q(card, '[data-dpd-point-text]');
    if (text) text.textContent = point || '';
    var box = q(card, '[data-dpd-chosen]');
    if (box) box.hidden = !chosen;
    var opener = q(card, '[data-dpd-open]');
    if (opener) opener.hidden = chosen;
    var sub = q(card, '[data-dpd-sub]');
    if (sub && !isOrderCard(card)) {
      sub.textContent = chosen
        ? "We'll send your order here:"
        : "Handy if no one's home. Shops and lockers near you, many open late and at weekends.";
    }
    if (chosen) showFinder(card, false);
  }

  // Every cart copy (drawer, sidebar, mobile footer) shows the same choice.
  function mirror(code, point, detail) {
    document.querySelectorAll('[data-dpd-pickup]').forEach(function (card) {
      if (isOrderCard(card)) return;
      applyChosen(card, code, point);
      renderDetail(card, code ? detail : null);
    });
    // The drawer's "Collecting from ..." line under the tabs (sections/mini-cart.liquid).
    document.querySelectorAll('[data-dpd-summary]').forEach(function (el) {
      el.hidden = !code;
      var text = q(el, '[data-dpd-summary-point]');
      if (text) text.textContent = point || '';
    });
  }

  function todayHours(p) {
    var i = (new Date().getDay() + 6) % 7;
    var h = (p.hours && p.hours[i]) || '';
    if (!h) return '';
    return h === 'Closed' ? 'Closed today' : (h === '24 hours' ? 'Open 24 hours today' : 'Today ' + h);
  }

  // Straight-line miles between two points (the order page knows the address's postcode centre).
  function milesBetween(a, b) {
    var rad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * rad;
    var dLng = (b.lng - a.lng) * rad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 3958.8 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }
  function formatMiles(mi) { return mi < 0.1 ? '0.1' : (mi < 10 ? mi.toFixed(1) : String(Math.round(mi))); }
  var cartHome = null;
  function fromHome(card, p) {
    var home = card.__home || (!isOrderCard(card) ? cartHome : null);
    return home && p && p.lat && p.lng ? formatMiles(milesBetween(home, p)) : null;
  }
  function compactPostcode(pc) { return String(pc || '').replace(/\s+/g, '').toUpperCase(); }
  function searchedAtHome(card, postcode) {
    var home = card.__homePostcode || (!isOrderCard(card) && cartHome ? compactPostcode(card.getAttribute('data-home-postcode')) : '');
    return !!home && compactPostcode(postcode) === home;
  }

  function renderResults(card, points, postcode) {
    resultsByCard.set(card, { points: points, postcode: postcode });
    var list = q(card, '[data-dpd-results]');
    if (!list) return;
    var current = card.getAttribute('data-code');
    var atHome = searchedAtHome(card, postcode);
    list.innerHTML = points.map(function (p, i) {
      // DPD's own figure when the search was the address itself (it matches DPD's order); our straight line otherwise.
      var home = atHome ? ((card.__home || (!isOrderCard(card) && cartHome)) ? esc(p.distance) : null) : fromHome(card, p);
      // With the address known, every point says how far it is from it; a search
      // elsewhere (near work, say) also gives the distance from that postcode.
      var dist = home
        ? '<span class="dpd-pick__dist">' + home + ' mi<small>from your address</small></span>'
        : '<span class="dpd-pick__dist">' + esc(p.distance) + ' mi<small>from ' + esc(postcode) + '</small></span>';
      var near = home && !atHome ? '<span class="dpd-pick__near">' + esc(p.distance) + ' mi from ' + esc(postcode) + '</span>' : '';
      return '<li><button type="button" class="dpd-pick__option" data-dpd-option="' + i + '" aria-pressed="' + (p.code === current) + '">' +
        '<span class="dpd-pick__name">' + esc(p.name) + '</span>' +
        dist +
        '<span class="dpd-pick__addr"><span class="dpd-pick__kind">' + (p.kind === 'locker' ? 'Locker' : 'Shop') + '</span>' +
        esc(p.street) + ', ' + esc(p.town) + ' ' + esc(p.postcode) + '</span>' +
        '<span class="dpd-pick__hours">' + esc(todayHours(p)) + (p.kind === 'locker' ? ' · collect with the code DPD texts you' : '') + (p.disabledAccess ? ' · step-free access' : '') + '</span>' +
        near +
        '</button></li>';
    }).join('');
  }

  // ---- Actions --------------------------------------------------------------
  // Lockers take parcels up to about 61 x 43 x 36 cm; bigger framed prints go
  // to shops only. The order page knows from the order; the cart sends its
  // SKUs and skylit-admin works it out (lockersFit).
  function lockerQuery(card) {
    if (isOrderCard(card)) return Promise.resolve(card.__lockersFit === false ? '&lockers=0' : '');
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (cart) {
        var skus = (cart.items || []).map(function (i) { return i.sku; }).filter(Boolean);
        return skus.length ? '&skus=' + encodeURIComponent(skus.join(',')) : '';
      })
      .catch(function () { return ''; });
  }

  function search(card) {
    var input = q(card, '[data-dpd-postcode]');
    var btn = q(card, '[data-dpd-find]');
    var postcode = (input && input.value || '').trim();
    if (!postcode) { setStatus(card, 'Enter a postcode to find pickup points near it.', true); return; }
    setStatus(card, 'Finding pickup points…');
    if (btn) btn.disabled = true;
    lockerQuery(card)
      .then(function (extra) {
        var home = !isOrderCard(card) && card.getAttribute('data-home-postcode');
        return fetch(API + '/points?postcode=' + encodeURIComponent(postcode) + extra + (home ? '&home=' + encodeURIComponent(home) : ''));
      })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body && res.body.error || 'Search failed');
        var points = res.body.points || [];
        if (!isOrderCard(card) && res.body.home) cartHome = res.body.home;
        if (input) input.value = res.body.postcode;
        remember(res.body.postcode);
        renderResults(card, points, res.body.postcode);
        var shopsOnly = res.body.lockersHidden ? ' Your order is too big for a locker, so these are shops.' : '';
        var where = searchedAtHome(card, res.body.postcode) ? 'your address (' + res.body.postcode + ')' : res.body.postcode;
        setStatus(card, (points.length
          ? points.length + ' pickup point' + (points.length === 1 ? '' : 's') + ' within 5 miles of ' + where + '. Tap one to choose it.'
          : 'No DPD pickup points within 5 miles of ' + where + '. Try a nearby postcode.') + shopsOnly, !points.length);
      })
      .catch(function (err) {
        renderResults(card, [], postcode);
        setStatus(card, err && err.message ? err.message : "We couldn't search just now. Please try again.", true);
      })
      .then(function () { if (btn) btn.disabled = false; });
  }

  function choose(card, index) {
    var state = resultsByCard.get(card);
    var p = state && state.points[index];
    if (!p) return;
    var point = lineFor(p);
    if (isOrderCard(card)) return saveToOrder(card, p, state.postcode);
    cachePoint(p);
    mirror(p.code, point, p);
    setStatus(card, '');
    saveToCart(p.code, point).then(function (ok) {
      if (!ok) {
        mirror('', '', null);
        showFinder(card, true);
        setStatus(card, "We couldn't save that choice. Please try again.", true);
      }
    });
  }

  function clearChoice(card) {
    mirror('', '', null);
    saveToCart('', '');
    showFinder(card, false);
  }

  // ---- After-checkout page (/pages/dpd-pickup) -----------------------------
  var DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  var TICK = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // The chosen point in full: kind, week of hours (today in bold), directions.
  function renderDetail(card, p, opts) {
    var box = q(card, '[data-dpd-detail]');
    if (!box) return;
    if (!p) { box.hidden = true; box.innerHTML = ''; return; }
    var today = (new Date().getDay() + 6) % 7;
    var table = (p.hours && p.hours.length === 7) ? '<table class="dpd-pick__hours-table"><caption class="visually-hidden">Opening hours</caption><tbody>' +
      p.hours.map(function (h, i) {
        return '<tr' + (i === today ? ' class="is-today"' : '') + '><th scope="row">' + DAYS[i] + '</th><td>' + esc(h) + '</td></tr>';
      }).join('') + '</tbody></table>' : '';
    // The cart has little room: today's hours, the week behind a toggle.
    var compact = !isOrderCard(card);
    var hours = compact && table
      ? '<p class="dpd-pick__today">' + esc(todayHours(p)) + '</p><details class="dpd-pick__week"><summary>Opening hours</summary>' + table + '</details>'
      : table;
    box.classList.toggle('dpd-pick__detail--compact', compact);
    var badges = [p.kind === 'locker' ? 'Locker · collect with the code DPD sends you' : 'Shop'];
    var home = fromHome(card, p);
    if (home) badges.push(home + ' mi from your address');
    if (p.disabledAccess) badges.push('Step-free access');
    box.innerHTML =
      (opts && opts.saved ? '<p class="dpd-pick__saved" role="status">' + TICK + 'Saved</p>' : '') +
      '<div class="dpd-pick__badges">' + badges.map(function (b) { return '<span class="dpd-pick__badge">' + esc(b) + '</span>'; }).join('') + '</div>' +
      hours +
      '<a class="dpd-pick__directions" href="https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(p.lat + ',' + p.lng) + '" target="_blank" rel="noopener">Get directions</a>' +
      (opts && opts.emailedTo ? '<p class="dpd-pick__confirm-note">We\'ve emailed a confirmation to ' + esc(opts.emailedTo) + '.</p>' : '');
    box.hidden = false;
  }

  // What's being collected, so the customer knows the link is theirs.
  function renderSummary(o) {
    var box = document.querySelector('[data-dpd-order]');
    if (!box || !o.items || !o.items.length) return;
    box.innerHTML = '<p class="dpd-page__order-head">Order ' + esc(o.name.replace(/^\D+/, '#')) + '</p>' +
      o.items.map(function (item) {
        var src = item.thumb || item.image;
        var img = src ? '<img class="dpd-page__thumb" src="' + esc(src) + '"' + (item.thumb && item.image ? ' data-fallback="' + esc(item.image) + '"' : '') + ' alt="" width="64" height="64" loading="lazy">' : '';
        var variant = item.variant ? '<span>' + esc(item.variant) + (item.quantity > 1 ? ' · ' + item.quantity + ' of them' : '') + '</span>' : '';
        // Product titles carry an SEO tail ("Custom Location Map | Personalised Map Print by Skylit Studio").
        return '<div class="dpd-page__item">' + img + '<div class="dpd-page__item-text">' + esc(String(item.title).split(' | ')[0]) + variant + '</div></div>';
      }).join('');
    // A design with no thumbnail yet falls back to the product photo.
    box.querySelectorAll('img[data-fallback]').forEach(function (img) {
      img.addEventListener('error', function () {
        var fallback = img.getAttribute('data-fallback');
        img.removeAttribute('data-fallback');
        if (fallback) img.src = fallback;
      }, { once: true });
    });
    box.hidden = false;
  }

  // The 2:30pm cut-off: with no point by then the order goes to the home
  // address, but until it's sent they can still choose one here.
  function renderDeadline(o) {
    var el = document.querySelector('[data-dpd-deadline]');
    if (!el) return;
    var text = '';
    if (o.cutoff && !o.code && !o.locked) {
      text = o.cutoffPassed
        ? "We didn't get a pickup point by the 2:30pm cut-off, so we'll send your order to your home address. It hasn't gone yet, so you can still choose one below."
        : 'Please choose by ' + o.cutoff + ". If we haven't heard from you by then, we'll send your order to your home address instead.";
    }
    el.textContent = text;
    el.hidden = !text;
  }
  function linkParams() {
    var params = new URLSearchParams(window.location.search);
    return { order: params.get('order') || '', token: params.get('token') || '' };
  }

  function saveToOrder(card, p, postcode) {
    var link = linkParams();
    setStatus(card, 'Saving…');
    fetch(API + '/order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: link.order, token: link.token, postcode: postcode, code: p.code })
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body && res.body.error || 'Saving failed');
        var o = res.body.order;
        applyChosen(card, o.code, o.point);
        renderDetail(card, o.detail, { saved: true, emailedTo: o.confirmationTo });
        renderDeadline(o);
        var title = q(card, '[data-dpd-title]');
        if (title) title.textContent = 'Your DPD Pickup point for order ' + o.name.replace(/^\D+/, '#');
        var sub = q(card, '[data-dpd-sub]');
        if (sub) sub.textContent = "We'll send order " + o.name.replace(/^\D+/, '#') + ' here. You can change it until we post it.';
        setStatus(card, '');
        var top = card.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.6) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
      .catch(function (err) { setStatus(card, err && err.message ? err.message : "We couldn't save that just now. Please try again.", true); });
  }

  function initOrderCard(card) {
    if (card.__dpdInited) return;
    card.__dpdInited = true;
    var link = linkParams();
    var title = q(card, '[data-dpd-title]');
    var sub = q(card, '[data-dpd-sub]');
    if (!link.order || !link.token) {
      if (sub) sub.textContent = "This page needs the link from our email. Reply to that email, or contact us, and we'll sort it.";
      return;
    }
    if (sub) sub.textContent = 'Looking up your order…';
    fetch(API + '/order?order=' + encodeURIComponent(link.order) + '&token=' + encodeURIComponent(link.token))
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body && res.body.error || 'Lookup failed');
        var o = res.body.order;
        var number = o.name.replace(/^\D+/, '#');
        card.__lockersFit = o.lockersFit;
        card.__home = o.home || null;
        card.__homePostcode = o.home ? compactPostcode(o.postcode) : '';
        renderSummary(o);
        renderDeadline(o);
        if (title) title.textContent = o.code
          ? 'Your DPD Pickup point for order ' + number
          : (o.firstName ? o.firstName + ', choose' : 'Choose') + ' a DPD Pickup point for order ' + number;
        if (o.locked) {
          if (sub) sub.textContent = 'Order ' + number + " has already been sent, so it can't be changed here. Track it from your dispatch email.";
          applyChosen(card, '', '');
          return;
        }
        if (o.pickupFits === false) {
          if (title) title.textContent = 'Order ' + number + ' is going to your address';
          if (sub) sub.textContent = "It's too big for a DPD Pickup shop or locker (50×70 and 60×80 framed prints are), so we'll deliver it to your address instead.";
          applyChosen(card, '', '');
          renderDeadline({});
          return;
        }
        if (o.code) {
          applyChosen(card, o.code, o.point);
          renderDetail(card, o.detail);
          if (sub) sub.textContent = "We'll send order " + number + ' here. Change it any time before we post it.';
        } else {
          if (sub) sub.textContent = o.postcode
            ? "Here are the nearest to your address. Search another postcode if you'd rather collect near work or family, then tap a shop" + (o.lockersFit === false ? '.' : ' or locker.')
            : "Search near where you'd like to collect, then tap a shop" + (o.lockersFit === false ? '.' : ' or locker.') + " We'll send your order there.";
          var input = q(card, '[data-dpd-postcode]');
          if (input && o.postcode) input.value = o.postcode;
          showFinder(card, true);
          if (input && input.value) search(card);
        }
      })
      .catch(function (err) {
        if (sub) sub.textContent = err && err.message ? err.message : "We couldn't find that order. Please reply to our email and we'll sort it.";
      });
  }

  // ---- Wiring ---------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var card = t.closest('[data-dpd-pickup]');
    if (!card) return;
    if (t.closest('[data-dpd-open]') || t.closest('[data-dpd-change]')) { e.preventDefault(); showFinder(card, true); return; }
    if (t.closest('[data-dpd-find]')) { e.preventDefault(); search(card); return; }
    if (t.closest('[data-dpd-remove]')) { e.preventDefault(); clearChoice(card); return; }
    var option = t.closest('[data-dpd-option]');
    if (option) { e.preventDefault(); choose(card, Number(option.getAttribute('data-dpd-option'))); }
  });

  // Enter in the postcode box searches; it must never submit the cart form.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target || !e.target.matches || !e.target.matches('[data-dpd-postcode]')) return;
    e.preventDefault();
    search(e.target.closest('[data-dpd-pickup]'));
  });

  var lookups = {};
  // A cart too big for a pickup point (50x70 / 60x80 framed) shows a note instead of the
  // finder; a point chosen before the big item went in is cleared so the order goes home.
  var clearingTooBig = false;
  function clearIfTooBig(card) {
    if (!card.hasAttribute('data-dpd-too-big') || !card.getAttribute('data-code') || clearingTooBig) return;
    clearingTooBig = true;
    card.setAttribute('data-code', '');
    mirror('', '', null);
    saveToCart('', '').then(function () { clearingTooBig = false; });
  }

  function hydrateCartCard(card) {
    if (card.hasAttribute('data-dpd-too-big')) return clearIfTooBig(card);
    if (isOrderCard(card) || card.__dpdHydrated) return;
    var code = card.getAttribute('data-code');
    if (!code) return;
    card.__dpdHydrated = true;
    var known = cachedPoint(code);
    if (known) { renderDetail(card, known); return; }
    var postcode = lineToPostcode(card.getAttribute('data-point'));
    if (!postcode) return;
    if (!lookups[code]) {
      lookups[code] = fetch(API + '/points?postcode=' + encodeURIComponent(postcode))
        .then(function (r) { return r.ok ? r.json() : { points: [] }; })
        .then(function (body) {
          var p = (body.points || []).filter(function (x) { return x.code === code; })[0] || null;
          if (p) cachePoint(p);
          return p;
        })
        .catch(function () { return null; });
    }
    lookups[code].then(function (p) { if (p && card.getAttribute('data-code') === code) renderDetail(card, p); });
  }

  function initAll() {
    document.querySelectorAll('[data-dpd-pickup][data-context="order"]').forEach(initOrderCard);
    document.querySelectorAll('[data-dpd-pickup]:not([data-context="order"])').forEach(hydrateCartCard);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // The cart drawer is re-rendered with innerHTML on every cart change; fill in its new cards.
  if (window.MutationObserver) {
    var pending = false;
    var addsCard = function (records) {
      return records.some(function (r) {
        return Array.prototype.some.call(r.addedNodes, function (n) {
          return n.nodeType === 1 && (n.matches('[data-dpd-pickup]') || !!n.querySelector('[data-dpd-pickup]'));
        });
      });
    };
    new MutationObserver(function (records) {
      if (pending || !addsCard(records)) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        document.querySelectorAll('[data-dpd-pickup]:not([data-context="order"])').forEach(hydrateCartCard);
      }, 50);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
